/**
 * Cliente HTTP para o backend orquestrador.
 *
 * A URL e a API key vêm de constantes injetadas em build-time pelo webpack
 * (ver src/env.d.ts e webpack.config.js):
 *  - dev: http://localhost:8001 (default)
 *  - prod: https://copiloto-norven.norven.com.br/api (addin/prod.env, via `npm run build:prod`):
 *    mesmo host do painel; o Ingress manda /api ao backend
 */
import { authHeader, forgetToken } from '../util/auth';
import { dlog } from '../util/log';
const SERVER_URL = __SERVER_URL__;
const API_KEY = __API_KEY__;
/**
 * Cabeçalhos comuns a todas as chamadas: a chave estática (quando ligada) e o
 * Bearer do Entra. São coisas diferentes — a `X-API-Key` barra acesso casual ao
 * endpoint, o Bearer diz QUEM está falando — e o backend usa cada uma para uma
 * decisão diferente.
 */
async function baseHeaders() {
    return Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (API_KEY ? { 'X-API-Key': API_KEY } : {})), (await authHeader()));
}
/**
 * Mensagem acionável do backend (`detail`), com o corpo cru como plano B.
 *
 * `detail` em LISTA é o 422 cru do FastAPI/Pydantic (um backend sem o
 * tratamento de `request_validation_handler` ainda o devolve assim). Mostrado
 * como veio, ele pôs `{"detail":[{"type":"int_from_float",...}]}` na tela de
 * quem só tinha pedido uma edição.
 */
async function errorDetail(resp) {
    const raw = await resp.text().catch(() => '');
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.detail === 'string')
            return parsed.detail;
        if (parsed && Array.isArray(parsed.detail))
            return describeRejectedFields(parsed.detail);
    }
    catch (_a) {
        /* corpo não-JSON (proxy, gateway): usa o texto como veio */
    }
    return raw || `Servidor respondeu ${resp.status} (${resp.statusText}).`;
}
/** Frase para a lista de erros de validação: QUAL campo, nunca o valor. */
function describeRejectedFields(errors) {
    const origens = new Set(['body', 'query', 'path', 'header', 'cookie']);
    const campos = errors
        .map((e) => {
        const loc = e === null || e === void 0 ? void 0 : e.loc;
        if (!Array.isArray(loc))
            return '';
        const partes = loc.map(String);
        return (origens.has(partes[0]) ? partes.slice(1) : partes).join('.');
    })
        .filter(Boolean);
    const onde = campos.length ? ` (${[...new Set(campos)].slice(0, 3).join(', ')})` : '';
    return (`O servidor não aceitou um dado enviado pelo painel${onde}. Não é um problema do seu ` +
        'documento — se continuar acontecendo, avise quem administra o mIAu.');
}
/**
 * `fetch` com os cabeçalhos padrão e UMA nova tentativa em 401.
 *
 * O 401 quase sempre é token vencido enquanto o painel estava aberto: o Office
 * reemite na hora, e sem esta retentativa o usuário veria "faça login de novo"
 * no meio de um documento por causa de um relógio.
 */
async function apiFetch(path, init = {}) {
    let foiComToken = false;
    const send = async () => {
        var _a;
        const headers = Object.assign(Object.assign({}, (await baseHeaders())), ((_a = init.headers) !== null && _a !== void 0 ? _a : {}));
        foiComToken = 'Authorization' in headers;
        return fetch(`${SERVER_URL}${path}`, Object.assign(Object.assign({}, init), { headers }));
    };
    let resp = await send();
    // Repete UMA vez só quando o 401 pode ser do token em cache (vencido ou
    // revogado). Se o 1º envio já saiu SEM token, é porque o Office não entregou
    // um agora mesmo — repetir era pedir de novo ao Office (até 8 chamadas e
    // ~22 s num throttling 13013) para receber o mesmo 401.
    if (resp.status === 401 && foiComToken) {
        forgetToken();
        resp = await send();
    }
    return resp;
}
/** Erro de uma chamada ao backend, com o status para quem precisa distinguir. */
export class ApiError extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
        this.name = 'ApiError';
    }
}
/**
 * Espera entre as repetições de um passo que falhou no gateway. Dois intervalos
 * = até três tentativas. Cobre o tempo de o Ingress trocar de pod durante um
 * rolling update (preStop de 10 s no backend) sem deixar o usuário esperando à
 * toa quando o problema é outro.
 */
export const STEP_RETRY_DELAYS_MS = [1500, 4000];
/**
 * O passo falhou no CAMINHO, não no backend? Então repetir é seguro.
 *
 * Seguro porque o `/agent/step` é stateless e o documento só muda DEPOIS que o
 * add-in recebe a resposta: repetir um passo perdido reenvia a mesma conversa a
 * outro pod, sem efeito colateral no Word. O pior caso é pagar a chamada ao
 * modelo duas vezes.
 *
 * Só 502 e 503, e só quando NÃO vieram do backend:
 *  - o backend responde erro sempre em JSON (`{detail}`). Um 502 em JSON é
 *    nosso — credencial recusada pela AWS — e repetir não resolve;
 *  - HTML ou corpo vazio vem do proxy: pod saindo, nenhum pod pronto ainda;
 *  - 504 NÃO entra: o proxy esperou o teto inteiro, e repetir dobraria uma
 *    espera que já foi longa demais;
 *  - 429 NÃO entra: é throttling, e o backend já repetiu com backoff.
 */
export function isTransientGatewayFailure(status, contentType) {
    if (status !== 502 && status !== 503)
        return false;
    return !(contentType !== null && contentType !== void 0 ? contentType : '').toLowerCase().includes('application/json');
}
function waitUnlessAborted(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal === null || signal === void 0 ? void 0 : signal.aborted)
            return reject(new DOMException('Aborted', 'AbortError'));
        const t = setTimeout(resolve, ms);
        signal === null || signal === void 0 ? void 0 : signal.addEventListener('abort', () => {
            clearTimeout(t);
            reject(new DOMException('Aborted', 'AbortError'));
        }, { once: true });
    });
}
/**
 * Teto do corpo da requisição — o MESMO do backend
 * (`MAX_REQUEST_BYTES` em app/middleware.py). Mantenha os dois iguais: aqui a
 * mensagem é acionável, lá é a proteção real do servidor.
 */
const MAX_REQUEST_BYTES = 50 * 1024 * 1024;
/**
 * Envia um request ao agent e recebe a resposta.
 * O backend executa um passo do loop (uma chamada ao Claude).
 */
export async function agentStep(req, signal, onRetry) {
    var _a, _b, _c, _d;
    dlog('[agentStep] POST /agent/step', {
        prompt_len: (_b = (_a = req.prompt) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0,
        has_selection: !!req.selection,
        msg_count: (_d = (_c = req.messages) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0,
        trace_id: req.correlation_id,
    });
    // Serializa ANTES de enviar para poder medir. O backend rejeita corpo acima
    // de 50MB (BodySizeLimitMiddleware) fechando a conexão, e nesse caminho o
    // `fetch` estoura como falha de rede genérica — o usuário veria "erro de
    // conexão" sem saber que o problema é tamanho. Medindo aqui, a mensagem diz
    // o que fazer. Documento muito longo + anexos é o caso que chega perto.
    const body = JSON.stringify(req);
    if (body.length > MAX_REQUEST_BYTES) {
        throw new Error(`O conteúdo desta mensagem (${Math.round(body.length / 1024 / 1024)}MB) passa do ` +
            `limite de ${MAX_REQUEST_BYTES / 1024 / 1024}MB por requisição. Remova algum anexo ` +
            'ou comece uma nova conversa para reduzir o histórico enviado.');
    }
    try {
        const send = () => apiFetch('/agent/step', {
            method: 'POST',
            headers: req.correlation_id ? { 'X-Correlation-ID': req.correlation_id } : {},
            body,
            signal,
        });
        // Repetição só para falha de CAMINHO (ver `isTransientGatewayFailure`):
        // pod saindo num rolling update, scale-down do HPA, conexão caída.
        let resp = null;
        for (let attempt = 0;; attempt++) {
            const podeRepetir = attempt < STEP_RETRY_DELAYS_MS.length;
            try {
                resp = await send();
            }
            catch (e) {
                // Parar é parar: o usuário pediu, nada de insistir.
                if ((signal === null || signal === void 0 ? void 0 : signal.aborted) || (e === null || e === void 0 ? void 0 : e.name) === 'AbortError' || !podeRepetir)
                    throw e;
                onRetry === null || onRetry === void 0 ? void 0 : onRetry({ attempt: attempt + 1, status: null });
                await waitUnlessAborted(STEP_RETRY_DELAYS_MS[attempt], signal);
                continue;
            }
            if (podeRepetir && isTransientGatewayFailure(resp.status, resp.headers.get('content-type'))) {
                onRetry === null || onRetry === void 0 ? void 0 : onRetry({ attempt: attempt + 1, status: resp.status });
                await waitUnlessAborted(STEP_RETRY_DELAYS_MS[attempt], signal);
                continue;
            }
            break;
        }
        if (!resp.ok) {
            // O backend traduz falhas do provedor em mensagens acionáveis (contexto
            // grande demais, sobrecarga, credencial). Elas vêm em `detail` — mostrar
            // o JSON cru ("Servidor respondeu 413: {\"detail\":...}") esconderia
            // justamente a frase que diz ao usuário o que fazer.
            throw new ApiError(await errorDetail(resp), resp.status);
        }
        const data = (await resp.json());
        dlog(`[agentStep] response:`, {
            stop_reason: data.stop_reason,
            content_blocks: data.content.length,
            usage: data.usage,
            trace_id: data.trace_id,
        });
        return data;
    }
    catch (error) {
        console.error('[agentStep] fetch error:', error);
        throw error;
    }
}
/**
 * Verifica saúde do backend.
 */
export async function getHealth() {
    const url = `${SERVER_URL}/health`;
    const resp = await fetch(url);
    if (!resp.ok)
        throw new Error(`Health check failed (${resp.status})`);
    return resp.json();
}
/**
 * Extrai texto estruturado de um DOCX anexado (POST /attachments/extract).
 * Markdown/TXT são lidos no cliente; PDF/imagem vão como blocos nativos —
 * só DOCX precisa deste round-trip (python-docx no backend, stateless).
 */
export async function extractAttachmentText(filename, dataBase64) {
    const resp = await apiFetch('/attachments/extract', {
        method: 'POST',
        body: JSON.stringify({ filename, data_base64: dataBase64 }),
    });
    // 422 traz a mensagem instrutiva do backend (ex.: ".doc não suportado").
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
    return resp.json();
}
/**
 * Valida a aderência de UMA imagem (base64, sem prefixo data:) à paleta
 * Norven via `POST /validate/image`. Usada pelo Validador do documento.
 */
export async function validateImage(imageBase64) {
    const resp = await apiFetch('/validate/image', {
        method: 'POST',
        body: JSON.stringify({ image_base64: imageBase64 }),
    });
    if (!resp.ok)
        throw new ApiError(`validate/image respondeu ${resp.status}`, resp.status);
    return resp.json();
}
/**
 * O que o backend oferece ANTES de qualquer login. Rota aberta de propósito: é
 * assim que o painel decide se deve pedir token ao Office (pedir num backend
 * sem SSO configurado abriria um erro do host sem explicação).
 */
export async function getAuthConfig() {
    const resp = await fetch(`${SERVER_URL}/auth/config`);
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
    return resp.json();
}
/** Confirma a identidade COM o backend (é ele quem valida o token, não nós). */
export async function getMe() {
    const resp = await apiFetch('/me');
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
    return resp.json();
}
/**
 * Registra o aceite da Política de Uso para o usuário do token.
 *
 * A VERSÃO é decidida pelo servidor: aceitar é aceitar o texto que ele publica.
 * `recorded=false` significa que não houve banco para gravar — o painel então
 * guarda o aceite localmente.
 */
export async function acceptPolicy() {
    const resp = await apiFetch('/policy/accept', { method: 'POST' });
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
    return resp.json();
}
/** As últimas conversas do usuário logado (para a tela de restauração). */
export async function listConversations() {
    var _a;
    const resp = await apiFetch('/conversations');
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
    const body = (await resp.json());
    return (_a = body.conversations) !== null && _a !== void 0 ? _a : [];
}
/** Conversa completa + OOXML do documento, para restaurar os dois juntos. */
export async function loadConversation(id) {
    const resp = await apiFetch(`/conversations/${encodeURIComponent(id)}`);
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
    return resp.json();
}
/** Grava o estado do painel. Idempotente por `id` (uma conversa = uma linha). */
export async function saveConversation(id, payload) {
    const resp = await apiFetch(`/conversations/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
    return resp.json();
}
/**
 * Renomeia só o título (`PATCH`, mais leve que `saveConversation`: não
 * reenvia mensagens nem documento — é só o texto do nome).
 */
export async function renameConversation(id, title) {
    const resp = await apiFetch(`/conversations/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
    });
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
    return resp.json();
}
/** Exclusão pedida pelo usuário (o corte das N últimas é automático). */
export async function deleteConversation(id) {
    const resp = await apiFetch(`/conversations/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
    if (!resp.ok)
        throw new ApiError(await errorDetail(resp), resp.status);
}
